import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { css } from 'emotion';
import ProcessValidator from 'utils/ProcessValidator';
import ModalsService from 'services/ModalsService';
import ValidationRollupModal from 'components/core/modals/ValidationRollupModal';
import Constants from 'utils/Constants';
import { getErrorPopoverStyle } from 'styles/base';


const errorStyles = css`
    &:hover {
        cursor: pointer;
    }
`;


/**
 * Renders a rollup error that shows all errors for a field and optionally its children
 */
export default class RollupError extends React.Component {
    static propTypes = {
        fieldPath: PropTypes.array, // the path to the root element in the data model
        processId: PropTypes.string,
        process: PropTypes.object, // needed for inputs outputs rollup
        processErrors: PropTypes.object, // the errors from the validation state
        includeChildren: PropTypes.bool, // should this error show child errors too, or just the root element?
        tooltipPosition: PropTypes.string,
        title: PropTypes.any, // object that the popover wraps around
        preventOnClick: PropTypes.bool
    };

    getWizardHideFields = () => {
        return [
            { fieldName: 'code_url', level: 1 },
            { fieldName: 'input_datasets', level: 1}
        ];
    };

    /**
     * Get all the process errors, but exclude warnings that aren't addressed in wizard mode
     */
    getProcessWizardRollupErrorList() {
        const errors = [];
        const hideFields = this.getWizardHideFields();
        if (this.props.processErrors) {
            Object.keys(this.props.processErrors).forEach(fieldPath => {
                let ignore = false;
                const path = fieldPath.split('.');
                hideFields.forEach(h => {
                    if (path[h.level] && path[h.level] === h.fieldName) {
                        ignore = true;

                    }
                });

                if (!ignore) {
                    errors.push(...this.props.processErrors[fieldPath]);
                }
            });
        }

        return errors;
    }

    /**
     * Get the error status for the top-level process information fields
     */
    getProcessDescriptionRollupErrorList() {
        const errors = [];
        if (this.props.processErrors) {
            Object.keys(this.props.processErrors).forEach(fieldPath => {
                if (fieldPath.split('.').length === 1) { // this is a top-level error

                    // Ignore input_datastreams, output_datastreams, & data_locations since they are displayed elsewhere
                    if (fieldPath !== 'input_datastreams' && fieldPath !== 'output_datastreams' && fieldPath !== 'data_locations') {

                        errors.push(...this.props.processErrors[fieldPath]);
                    }
                } else if (fieldPath === 'ui_state.code_url') {
                    errors.push(...this.props.processErrors[fieldPath]);
                }
            });

        }
        return errors;
    }

    getInputsOutputsRollupErrorList() {
        const errors = [];
        if (this.props.processErrors) {
            Object.keys(this.props.processErrors).forEach(fieldPath => {;
                if (_.isEmpty(this.props.process.variable_retrieval)) {
                    if (fieldPath.startsWith('input_datastreams') || fieldPath.startsWith('output_datastreams')) {
                        errors.push(...this.props.processErrors[fieldPath]);
                    }

                } else if (fieldPath.startsWith('variable_retrieval') || fieldPath.startsWith('output_datastreams')) {
                    errors.push(...this.props.processErrors[fieldPath]);
                }
            });

        }
        return errors;
    }

    getOutputDatastreamsRollupErrorList() {
        const errors = [];
        if (this.props.processErrors) {
            Object.keys(this.props.processErrors).forEach(fieldPath => {;
                if (fieldPath.startsWith('variable_retrieval.output_datastreams') || fieldPath.startsWith('variable_retrieval.output_datastream_variable_mappings')) {
                    errors.push(...this.props.processErrors[fieldPath]);
                }
            });

        }
        return errors;
    }

    /**
     * Get the rollup error class for top-level variable retrieval errors that show up on the summary view. These are errors
     * like "at least one variable must be defined".
     */
    getSummaryErrorClassName() {
        let maxError = null;
        if (this.props.processErrors) {
            const errors = [];

            Object.keys(this.props.processErrors).forEach(fieldPath => {

                // We are not including input dataset errors here because they show up on the retrieval rules page
                if (fieldPath === 'output_datastreams' || fieldPath === 'variable_retrieval' ||
                    fieldPath === 'variable_retrieval.retrieved_variables' || fieldPath === 'variable_retrieval.coordinate_systems') {
                    errors.push(this.props.processErrors[fieldPath]);
                }
            });

            maxError = ProcessValidator.getRollupError(errors, this.getProcessName());
        }
        const errorClass = ProcessValidator.getErrorClassName(maxError);
        return errorClass;
    }

    /**
     * @param errorPath
     * @param includeRoot - We only care about errors that fall underneath the root path, not at the root path
     */
    getRollupErrorClassName(errorPath, includeRoot) {
        let maxError = null;
        if (this.props.processErrors && errorPath && errorPath.length > 0) {
            const errorPathPrefix = errorPath.join('.');
            const subPathPrefix = `${errorPathPrefix}.`;

            const errors = [];
            Object.keys(this.props.processErrors).forEach(fieldPath => {
                if ((includeRoot && fieldPath === errorPathPrefix) || fieldPath.startsWith(subPathPrefix)) {
                    errors.push(this.props.processErrors[fieldPath]);
                }
            });
            maxError = ProcessValidator.getRollupError(errors, this.getProcessName());
        }
        const errorClass = ProcessValidator.getErrorClassName(maxError);
        return errorClass;
    }

    getErrorList() {
        let errorList = [];
        if (this.props.fieldPath) {
            // First check if this is a special error category where we have to pick and choose errors
            if (this.props.fieldPath.length === 1 && this.props.fieldPath[0] === 'all') {
                errorList = Object.values(this.props.processErrors).map(err => {
                    return err[0];
                });

            } else if (this.props.fieldPath.length === 1 && this.props.fieldPath[0] === 'all-wizard') {
                errorList = this.getProcessWizardRollupErrorList();

            } else if (this.props.fieldPath.length === 1 && this.props.fieldPath[0] === 'process-info') {
                errorList = this.getProcessDescriptionRollupErrorList();

            } else if (this.props.fieldPath.length === 1 && this.props.fieldPath[0] === 'inputs-outputs') {
                errorList = this.getInputsOutputsRollupErrorList();

            } else if (this.props.fieldPath[0] === 'output_datastreams' && this.props.includeChildren) {
                errorList = this.getOutputDatastreamsRollupErrorList();

            } else if (this.props.fieldPath[0] === 'variable_retrieval' && this.props.fieldPath[1] === 'retrieved_variables') {
                errorList = ProcessValidator.getErrorListForField(this.props.processErrors, this.props.fieldPath, this.props.includeChildren);

                if (this.props.process) {
                    if (this.props.fieldPath[2]) {
                        const variableName = this.props.fieldPath[2];
                        const inputDatasetName = this.props.process.variable_retrieval.retrieved_variables[variableName].input_dataset;
                        this.props.process.variable_retrieval.input_datasets[inputDatasetName].rules.forEach((rule, index) => {
                            const retrievalFieldPath = ['variable_retrieval', 'input_datasets', inputDatasetName, 'rules', index, 'variable_mappings', variableName];
                            const retrievalErrorList = ProcessValidator.getErrorListForField(this.props.processErrors, retrievalFieldPath, this.props.includeChildren);
                            errorList = [...errorList, ...retrievalErrorList];
                        });
                    } else {
                        //get it for rollup as well
                        Object.keys(this.props.process.variable_retrieval.retrieved_variables).forEach(variableName => {
                            const inputDatasetName = this.props.process.variable_retrieval.retrieved_variables[variableName].input_dataset;
                            this.props.process.variable_retrieval.input_datasets[inputDatasetName].rules.forEach((rule, index) => {
                                const retrievalFieldPath = ['variable_retrieval', 'input_datasets', inputDatasetName, 'rules', index, 'variable_mappings', variableName];
                                const retrievalErrorList = ProcessValidator.getErrorListForField(this.props.processErrors, retrievalFieldPath, this.props.includeChildren);
                                errorList = [...errorList, ...retrievalErrorList];
                            });
                        })
                    }
                }
            } else if (this.props.fieldPath[0] === 'wizard') {
                errorList = ProcessValidator.getWizardStepRollupErrorList(this.props.processErrors, this.props.fieldPath[1], this.props.fieldPath[2])

            } else {
                errorList = ProcessValidator.getErrorListForField(this.props.processErrors, this.props.fieldPath, this.props.includeChildren);
            }
        }

        return errorList;
    }

    getErrorClass(errorList) {
        const maxError = ProcessValidator.getRollupError(errorList, this.getProcessName());
        return ProcessValidator.getErrorClassName(maxError);
    }

    getProcessName = () => {
        if (this.props.process) {
            return this.props.process.name
        }
        return this.props.processId;
    };

    getLevelCountMessage(errorList) {
        let messages = [];
        const levels = [Constants.LEVEL_DISALLOW, Constants.LEVEL_ERROR, Constants.LEVEL_WARNING];
        levels.forEach(level => {
            let count = 0;
            errorList.forEach(err => {
                if (err.level === level) {
                    count++;
                }
            });

            if (count > 0) {
                messages.push(`${count} ${level}${count > 1 ? 's' : ''}`);
            }
        });

        return `${messages.join(', ')}.`;
    }

    openModal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const modalTitle = 'Validation Report';

        const modalComponent = (
            <ValidationRollupModal
                processId={this.getProcessName()}
                errorList={this.getErrorList()}
            />
        );
        ModalsService.openModal({
            id: ModalsService.TYPE_VALIDATION,
            modalType: ModalsService.TYPE_CUSTOM,
            modalTitle,
            modalComponent,
            modalSize: 'lg'
        });
    };

    render() {
        const title = this.props.title ? this.props.title : '';

        if (this.props.fieldPath) {
            const errorList = this.getErrorList();
            const errorClass = this.getErrorClass(errorList);

            if (errorList.length > 0) {
                const counts = this.getLevelCountMessage(errorList);
                const hoverMsg = `${counts} Click to view validation report.`;

                if (this.props.preventOnClick) {
                    return (
                        <span className={`${errorClass} ${errorStyles}`} >{title}</span>
                    );
                } else {
                    return (
                        <span className={`${errorClass} ${errorStyles}`} onClick={this.openModal} title={hoverMsg}>{title}</span>
                    );
                }
            }
        }

        if (this.props.title) {
            return <div>{title}</div>;
        }

        return null;
    }
}



// WEBPACK FOOTER //
// ./src/components/core/RollupError.js